/**
 * Boîte à outils — défis à choix multiple des outils de physique-chimie (circuit, mouvement…) :
 * types, tirage, options mélangées et écriture des nombres à la française. Logique PURE.
 */

export type Rng = () => number;

export interface McqOption {
  text: string;
  correct: boolean;
  /** pourquoi c'est faux */
  hint?: string;
}

export interface Mcq<K extends string = string> {
  kind: K;
  question: string;
  options: McqOption[];
  explain: string;
}

export const pick = <T>(xs: readonly T[], rng: Rng): T => xs[Math.floor(rng() * xs.length)]!;

export function shuffle<T>(xs: readonly T[], rng: Rng): T[] {
  const a = [...xs];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/** options : la bonne + des fausses distinctes (après écriture), mélangées ; 3 fausses au plus */
export function mcqOptions(
  good: string,
  wrongs: ReadonlyArray<readonly [string, string]>,
  rng: Rng,
): McqOption[] {
  const seen = new Set([good]);
  const ws: McqOption[] = [];
  for (const [text, hint] of wrongs) {
    if (seen.has(text)) continue;
    seen.add(text);
    ws.push({ text, correct: false, hint });
  }
  return shuffle([{ text: good, correct: true }, ...ws.slice(0, 3)], rng);
}

/** une question ; nouveau tirage tant que deux choix s'écrivent pareil (il en faut `min`) */
export function retryMcq<K extends string>(build: (rng: Rng) => Mcq<K>, rng: Rng, min = 4): Mcq<K> {
  let q = build(rng);
  for (let i = 0; i < 30 && q.options.length < min; i++) q = build(rng);
  return q;
}

const NNB = String.fromCharCode(0x202f);

/** nombre à la française, `d` décimales au plus (zéros inutiles retirés), « 18 000 » */
export function frNum(v: number, d = 2): string {
  if (!Number.isFinite(v)) return '∞';
  const r = Number(v.toFixed(d));
  const [int, dec] = String(Math.abs(Object.is(r, -0) ? 0 : r)).split('.');
  const grouped = int!.length > 3 ? int!.replace(/\B(?=(\d{3})+(?!\d))/g, NNB) : int!;
  return `${r < 0 ? '−' : ''}${grouped}${dec ? `,${dec}` : ''}`;
}

/** `s` chiffres significatifs (3 par défaut), virgule */
export function sigNum(v: number, s = 3): string {
  if (!Number.isFinite(v)) return '∞';
  if (v === 0) return '0';
  const d = Math.max(0, s - 1 - Math.floor(Math.log10(Math.abs(v))));
  return frNum(v, d);
}

/** `s` chiffres significatifs en gardant les zéros (« 2,0 », « 0,980 ») — écriture d'un résultat */
export function sigFixed(v: number, s = 2): string {
  if (!Number.isFinite(v)) return '∞';
  if (v === 0) return '0';
  const d = Math.max(0, s - 1 - Math.floor(Math.log10(Math.abs(v))));
  const r = Number(v.toPrecision(s));
  const t = Math.abs(r).toFixed(d);
  const [int, dec] = t.split('.');
  const grouped = int!.length > 3 ? int!.replace(/\B(?=(\d{3})+(?!\d))/g, NNB) : int!;
  return `${r < 0 ? '−' : ''}${grouped}${dec ? `,${dec}` : ''}`;
}
